import { lazy, Suspense, useEffect } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router";
import { CarmenLanding } from "@/components/marketing/CarmenLanding";
import { SeoMetadata } from "@/components/SeoMetadata";
import { AboutPage } from "@/routes/AboutPage";
import { ContactPage } from "@/routes/ContactPage";
import { DownloadPage } from "@/routes/DownloadPage";
import { RichtlijnenPage } from "@/routes/RichtlijnenPage";
import { PrivacyPage } from "@/routes/PrivacyPage";
import { TermsPage } from "@/routes/TermsPage";
import { TrustPage } from "@/routes/TrustPage";
import { SubProcessorsPage } from "@/routes/SubProcessorsPage";
import { VerifiedPage } from "@/routes/VerifiedPage";
import { NotFoundPage } from "@/routes/NotFoundPage";
import { LoginPage } from "@/routes/LoginPage";
import { EmailLoginPage } from "@/routes/EmailLoginPage";
import { SignupPage } from "@/routes/SignupPage";
import { PasskeyHandoffPage } from "@/routes/PasskeyHandoffPage";
import { RecoverPage } from "@/routes/RecoverPage";
import { ResetRecoverPage } from "@/routes/ResetRecoverPage";
import { WidgetEmailLoginPage } from "@/routes/WidgetEmailLoginPage";
import { AccountPage } from "@/routes/AccountPage";
import { AdminPage } from "@/routes/AdminPage";
import { PublicPageLayout } from "@/components/PublicPageLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Toaster } from "@/components/Toaster";
import { SupportChat } from "@/components/SupportChat";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { browserReloadDeps, importWithReload } from "@/lib/chunkReload";
import { clearOlderThan } from "@/lib/pendingUploads";
import { useAuth } from "@/lib/hooks/useAuth";

// Lazy: the page bundles every @carmen/i18n catalogue; keep them out of the main chunk.
// importWithReload: a chunk 404 during a frontend rollout reloads instead of blanking.
const WidgetPasskeyLoginPage = lazy(() =>
  importWithReload(
    () => import("@/routes/WidgetPasskeyLoginPage"),
    browserReloadDeps("widget-passkey-login"),
  ).then((m) => ({ default: m.WidgetPasskeyLoginPage })),
);

const LEGACY_HEADER_PATHS = new Set([
  "/verified",
]);

const SUPPORT_CHAT_PATHS = new Set([
  "/",
  "/about",
  "/contact",
  "/download",
  "/privacy",
  "/terms",
  "/trust",
  "/sub-processors",
  "/login",
  "/signup",
  "/recover",
]);

export function App() {
  const auth = useAuth();
  const location = useLocation();
  const showAppHeader = LEGACY_HEADER_PATHS.has(location.pathname);

  useEffect(() => {
    if (location.hash) {
      const animationFrame = window.requestAnimationFrame(() => {
        const target = document.getElementById(location.hash.slice(1));
        target?.scrollIntoView({ block: "start", behavior: "auto" });
      });

      return () => window.cancelAnimationFrame(animationFrame);
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return undefined;
  }, [location.pathname, location.hash]);

  // Purge stashed retry blobs past their TTL at startup, so PHI audio doesn't
  // accumulate in IndexedDB regardless of whether the recordings list is opened.
  useEffect(() => {
    void clearOlderThan().catch(() => {
      /* best effort */
    });
  }, []);

  return (
    <div className="min-h-screen">
      <SeoMetadata />
      <ImpersonationBanner />
      {showAppHeader ? (
        <header className="border-b border-[rgba(10,52,96,0.10)] bg-[rgba(255,255,255,0.92)] backdrop-blur-xl">
          <nav className="flex items-center gap-6 px-6 py-5">
            <Link to="/" className="flex items-center gap-3 text-lg font-semibold text-[var(--cs-navy)]">
              <img src="/carmen-logo.png" alt="Carmen" className="h-10 w-auto" />
            </Link>
            <div className="ml-auto flex items-center gap-3">
              {!auth.loading && !auth.user && location.pathname !== "/login" ? (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-full border border-[rgba(10,52,96,0.10)] bg-white px-5 py-2.5 text-sm font-medium text-[var(--cs-navy)] shadow-[0_12px_28px_rgba(10,52,96,0.08)] transition-all duration-200 hover:-translate-y-[1px]"
                >
                  Inloggen
                </Link>
              ) : null}
              {!auth.loading && !auth.user && location.pathname !== "/signup" ? (
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-full bg-[var(--cs-navy)] px-5 py-2.5 text-sm font-medium text-white shadow-[0_12px_28px_rgba(10,52,96,0.18)] transition-all duration-200 hover:-translate-y-[1px] hover:bg-[var(--cs-navy-strong)]"
                >
                  registreer
                </Link>
              ) : null}
            </div>
          </nav>
        </header>
      ) : null}
      <main>
        <Routes>
          <Route path="/" element={<CarmenLanding />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/record" element={<Navigate to="/download" replace />} />
          <Route path="/mobile-recorder" element={<Navigate to="/download" replace />} />
          <Route
            path="/richtlijnen"
            element={
              <RequireAuth>
                <RichtlijnenPage />
              </RequireAuth>
            }
          />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/trust" element={<TrustPage />} />
          <Route path="/sub-processors" element={<SubProcessorsPage />} />
          <Route path="/verified" element={<VerifiedPage />} />
          <Route
            path="/login"
            element={
              <PublicPageLayout>
                <LoginPage />
              </PublicPageLayout>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicPageLayout>
                <SignupPage />
              </PublicPageLayout>
            }
          />
          <Route path="/passkey-handoff" element={<PasskeyHandoffPage />} />
          <Route
            path="/widget-passkey-login"
            element={
              <Suspense fallback={null}>
                <WidgetPasskeyLoginPage />
              </Suspense>
            }
          />
          <Route path="/login/email" element={<EmailLoginPage />} />
          <Route path="/widget-email-login" element={<WidgetEmailLoginPage />} />
          <Route path="/recover" element={<RecoverPage />} />
          <Route path="/recover/reset" element={<ResetRecoverPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/it-overview" element={<Navigate to="/admin" replace />} />
          <Route path="/itoverview" element={<Navigate to="/admin" replace />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {SUPPORT_CHAT_PATHS.has(location.pathname) ? <SupportChat /> : null}
      <Toaster />
    </div>
  );
}
